import { reviver } from '@thinkalpha/ui-core/serializer';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { Decoder, Encoder } from 'socket.io-parser';
import { container } from 'src/StaticContainer';

// Resolved lazily so importing this module (e.g. in a unit test) doesn't
// require the DI container to be configured — only code paths that
// actually log touch it.
const getLog = () => container.get('Logger').getSubLogger({ name: 'InventoryUploadStream' }, { component: 'lab' });

// The locates-ws push contract for inventory uploads. These mirror the
// backend's `RoomPrefix.InventoryUpload` room and item event. They live
// here (not imported from `@thinkalpha/locates-ws-client`) because that
// package only publishes the HTTP contract surface, not the WS events.
const INVENTORY_UPLOAD_ROOM_TYPE = 'v1/inventory/pools/items';
const INVENTORY_ITEM_EVENT = 'v1.inventory.pools.item';

/** A single item streamed as it lands during a pool inventory upload. */
export interface InventoryUploadItem {
    id: string;
    uploadId: string;
    poolId: string;
    symbol: string;
    quantity: number;
    price: number;
}

export interface InventoryUploadStreamOptions {
    /** locates-ws socket URI (from `appConfig.locatesApi`). */
    uri: string;
    accessToken: string | undefined;
    /** Pool whose upload room to join. */
    poolId: string;
    /** Called once per distinct streamed item for the pool. */
    onItem: (item: InventoryUploadItem) => void;
    /**
     * Test seam: supply a socket instead of opening a real one. Defaults
     * to a websocket connected to {@link InventoryUploadStreamOptions.uri}.
     */
    socketFactory?: (uri: string) => Socket;
}

export interface InventoryUploadStreamHandle extends Disposable {
    /**
     * Resolve `true` once the socket has authenticated and joined the
     * pool's upload room, or `false` if authentication fails, the stream
     * is disposed, or `timeoutMs` elapses first. Lets the caller skip
     * {@link waitForItems} and fall back to a non-streamed progress path
     * instead of blocking on item events that will never arrive when the
     * socket never becomes usable (missing/rejected token, no connection).
     * Resolves immediately once the ready state has settled.
     */
    whenReady(timeoutMs: number): Promise<boolean>;
    /**
     * Resolve once `expected` distinct items for the pool have streamed
     * in. Falls back to resolving after `idleTimeoutMs` elapses with no
     * new item — a safety net for a dropped event or an `expected` the
     * stream never reaches, without cutting off an actively-arriving
     * batch. Resolves immediately when `expected <= 0` or already
     * satisfied. The idle timer resets on every new item.
     */
    waitForItems(expected: number, idleTimeoutMs: number): Promise<void>;
}

function defaultSocketFactory(uri: string): Socket {
    // Mirrors `createLocatesSocket` (store/channels/util) but without the
    // redux-saga imports that module pulls in.
    return io(uri, {
        timeout: 10000,
        transports: ['websocket'],
        parser: {
            Encoder,
            Decoder: class extends Decoder {
                constructor() {
                    super(reviver);
                }
            },
        },
        reconnectionDelay: 500,
        reconnectionDelayMax: 60000,
        randomizationFactor: 0.1,
    });
}

/**
 * Subscribe to the live inventory-upload item stream for a pool. The
 * caller drives progress from {@link InventoryUploadStreamOptions.onItem}
 * and awaits {@link InventoryUploadStreamHandle.waitForItems} to know
 * when the backend has finished emitting a batch.
 */
export function openInventoryUploadStream(opts: InventoryUploadStreamOptions): InventoryUploadStreamHandle {
    const socket = (opts.socketFactory ?? defaultSocketFactory)(opts.uri);
    let disposed = false;
    // Distinct item ids seen for this pool, so a re-emit can't double-count.
    // Keyed by item id rather than uploadId: the room is already scoped to
    // this pool, and the streamed items' uploadId does not reliably match
    // the POST ack's uploadId, so filtering on it drops every event.
    const seen = new Set<string>();
    // Each waiter resolves when the count is reached; `bumpIdle` restarts
    // its idle timer so an actively-streaming batch is never cut short.
    const waiters: Array<{ expected: number; resolve: () => void; bumpIdle: () => void }> = [];

    // Ready = authenticated + joined the pool room. `null` while pending,
    // then latched to the settled result so `whenReady` can answer late
    // callers without re-waiting.
    let ready: boolean | null = null;
    const readyWaiters: Array<(ok: boolean) => void> = [];
    const settleReady = (ok: boolean): void => {
        if (ready !== null) return;
        ready = ok;
        for (const resolve of readyWaiters.splice(0)) resolve(ok);
    };

    const onItemProgress = (): void => {
        for (let i = waiters.length - 1; i >= 0; i -= 1) {
            const w = waiters[i]!;
            if (seen.size >= w.expected) {
                waiters.splice(i, 1);
                w.resolve();
            } else {
                w.bumpIdle();
            }
        }
    };

    socket.on('connect', () => {
        if (TRACE) {
            getLog().trace('Socket connected, authenticating');
        }
        socket.emit('auth', opts.accessToken);
    });

    socket.on('auth.success', () => {
        if (TRACE) {
            getLog().trace('Authenticated, joining inventory upload room', { poolId: opts.poolId });
        }
        // Clear any prior room membership, then join this pool's upload room.
        socket.emit('room.leave');
        socket.emit('room.join', { type: INVENTORY_UPLOAD_ROOM_TYPE, poolId: opts.poolId });
        settleReady(true);
    });

    socket.on('auth.error', (error: unknown) => {
        getLog().error({
            message: 'Inventory upload stream authentication failed',
            error: error instanceof Error ? error : new Error(String(error)),
        });
        settleReady(false);
    });

    socket.on('connect_error', (error: unknown) => {
        getLog().error({
            message: 'Inventory upload stream connection failed',
            error: error instanceof Error ? error : new Error(String(error)),
        });
    });

    socket.on(INVENTORY_ITEM_EVENT, (payload: { item: InventoryUploadItem }) => {
        if (disposed) return;
        const item = payload?.item;
        if (!item || item.poolId !== opts.poolId) return;
        if (seen.has(item.id)) return;
        seen.add(item.id);
        opts.onItem(item);
        onItemProgress();
    });

    if (!socket.active) {
        socket.connect();
    }

    return {
        whenReady(timeoutMs) {
            if (ready !== null) return Promise.resolve(ready);
            return new Promise<boolean>((resolve) => {
                let settled = false;
                const timer = setTimeout(() => {
                    if (settled) return;
                    settled = true;
                    resolve(false);
                }, timeoutMs);
                // Left in `readyWaiters` after a timeout but guarded by
                // `settled`; dispose/settle clears the array either way.
                const cb = (ok: boolean): void => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    resolve(ok);
                };
                readyWaiters.push(cb);
            });
        },
        waitForItems(expected, idleTimeoutMs) {
            if (expected <= 0 || seen.size >= expected) return Promise.resolve();
            return new Promise<void>((resolve) => {
                let timer: ReturnType<typeof setTimeout>;
                const finish = (): void => {
                    clearTimeout(timer);
                    const idx = waiters.indexOf(waiter);
                    if (idx >= 0) waiters.splice(idx, 1);
                    resolve();
                };
                // (Re)arm the idle timer — fires only after a full quiet
                // window with no new item.
                const bumpIdle = (): void => {
                    clearTimeout(timer);
                    timer = setTimeout(finish, idleTimeoutMs);
                };
                const waiter = { expected, resolve: finish, bumpIdle };
                waiters.push(waiter);
                bumpIdle();
            });
        },
        [Symbol.dispose]() {
            if (disposed) return;
            disposed = true;
            // Unblock a caller still awaiting readiness — a disposed stream
            // will never join, so report it as not ready.
            settleReady(false);
            for (const w of waiters.splice(0)) w.resolve();
            socket.removeAllListeners();
            // `room.leave` needs an established connection, but `disconnect()`
            // must run unconditionally — otherwise disposing mid-handshake
            // leaves the pending connection (and its reconnection attempts)
            // alive after the caller believes the stream is torn down.
            if (socket.connected) {
                socket.emit('room.leave');
            }
            socket.disconnect();
        },
    };
}
